import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '../components/sections/Breadcrumb';
import { winnersApi, categoriesApi } from '../services/api';
import type { Winner as ApiWinner } from '../types';
import { mapYearIdsToYears, yearToYearId } from '../utils/yearMapper';

// Constants to prevent repeated URL generation
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM2QjczODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5UREEgV2lubmVyPC90ZXh0Pgo8L3N2Zz4K';

interface WinnerType {
  id: number;
  name: string;
  organization: string;
  description: string;
  image: string;
}


type Winner = {
  id: number;
  name: string;
  organization: string;
  description: string;
  image: string;
};

type Subcategory = {
  name: string;
  winner: Winner;
};

type Category = {
  category: string;
  subcategories: Subcategory[];
};

const Winners = () => {
  const { year } = useParams<{ year: string }>();
  const [winners, setWinners] = useState<Category[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (isMounted) {
          setLoading(true);
        }
        
        // Fetch available years (these are database IDs: 1, 2, 3, 4)
        const dbYears = await winnersApi.getYears();
        
        // Convert database year IDs to actual years (2021, 2022, 2023, 2024)
        const actualYears = mapYearIdsToYears(dbYears);
        
        if (!isMounted) return;
        
        setYears(actualYears);
        
        // Determine which year to display
        const displayYear = year || actualYears[0]; // Default to most recent year
        setSelectedYear(displayYear);
        
        // Convert display year to database year ID for API call
        const dbYearId = yearToYearId(displayYear);
        
        // Get winners for selected year using database year ID
        const winnersData = await winnersApi.getByYear(dbYearId);
        
        if (!isMounted) return;
        
        // Fetch categories and subcategories to get names
        const allCategories = await categoriesApi.getAll();
        const categoryMap = new Map(allCategories.map(cat => [cat.id.toString(), cat.name]));
        
        // Create a map for subcategories
        const subcategoryMap = new Map();
        for (const category of allCategories) {
          try {
            const subcategories = await categoriesApi.getSubcategories(category.id);
            subcategories.forEach(sub => {
              subcategoryMap.set(`${category.id}-${sub.id}`, sub.name);
            });
          } catch (error) {
            console.warn(`Failed to fetch subcategories for category ${category.id}:`, error);
          }
        }

        // Transform the data to match the expected format with proper names
        const transformedWinners: Category[] = Object.entries(winnersData).map(([categoryId, winners]) => {
          const categoryName = categoryMap.get(categoryId) || `Category ${categoryId}`;
          
          return {
            category: categoryName,
            subcategories: winners.map((winner: ApiWinner) => {
              const subcategoryKey = `${winner.category}-${winner.subcategory}`;
              const subcategoryName = subcategoryMap.get(subcategoryKey) || `Subcategory ${winner.subcategory}`;
              
              return {
                name: subcategoryName,
                winner: {
                  id: winner.id,
                  name: winner.name,
                  organization: winner.organization || categoryName,
                  description: winner.description || `Winner in ${categoryName} - ${subcategoryName} - ${displayYear}`,
                  image: winner.image ? `/assets/posters/${winner.image}` : PLACEHOLDER_IMAGE
                }
              };
            })
          };
        });
        
        setWinners(transformedWinners);
        
      } catch (error) {
        if (isMounted) {
          console.error('Failed to fetch winners:', error);
          setWinners([]);
        }
      } finally {
        if (isMounted) {
    setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [year]);

  // Memoize the category change handler
  const handleCategoryChange = useCallback((categoryIndex: number) => {
    setSelectedCategory(categoryIndex);
  }, []);

  // Memoize the winners data to prevent unnecessary re-renders
  const memoizedWinners = useMemo(() => winners, [winners]);

  // Early return if loading to prevent rendering issues
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Breadcrumb title="Winners" subtitle="WINNERS" />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0f1b3c' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Breadcrumb title={`${selectedYear} Winners`} subtitle="WINNERS" />

      {/* Year Navigation */}
      <section 
        className="py-8 sm:py-10 md:py-12 bg-white"
        style={{
          padding: '2rem 0',
          backgroundColor: '#ffffff'
        }}
      >
        <div 
          className="container mx-auto px-4"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}
        >
          <div 
            className="max-w-6xl mx-auto"
            style={{
              maxWidth: '72rem',
              margin: '0 auto'
            }}
          >
            <div 
              className="text-center mb-6 sm:mb-10"
              style={{
                textAlign: 'center',
                marginBottom: '1.5rem'
              }}
            >
              <h2 
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6"
                style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '1rem',
                  lineHeight: '1.2'
                }}
              >
                Explore Our Digital Champions
              </h2>
              <p 
                className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto text-left"
                style={{
                  fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                  color: '#4B5563',
                  maxWidth: '48rem',
                  margin: '0 auto',
                  lineHeight: '1.75'
                }}
              >
                Discover the outstanding individuals and organizations that have been recognized for their excellence in digital innovation and transformation across Tanzania.
              </p>
            </div>

            {/* Mobile Year Selector */}
            <div className="block sm:hidden mb-6 relative">
              <select
                className="w-full py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg appearance-none text-gray-800 font-medium pr-10"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  paddingRight: '2.5rem',
                  backgroundColor: '#F9FAFB',
                  borderColor: '#E5E7EB',
                  borderRadius: '0.5rem',
                  appearance: 'none',
                  color: '#1F2937',
                  fontWeight: '500',
                  position: 'relative'
                }}
                value={selectedYear}
                onChange={(e) => {
                  const year = e.target.value;
                  window.location.href = `/winners/${year}`;
                }}
              >
                {years.map((yearItem) => (
                  <option key={`year-option-${yearItem}`} value={yearItem}>
                    {yearItem} Winners
                  </option>
                ))}
              </select>
              <div 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 flex items-center justify-center"
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px'
                }}
              >
                <i className="fa-solid fa-chevron-down text-sm" style={{ fontSize: '0.875rem' }}></i>
              </div>
            </div>

            {/* Desktop Year Navigation */}
            <div 
              className="hidden sm:flex flex-wrap justify-center gap-3 md:gap-4 mb-8 md:mb-12"
              style={{
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '0.75rem',
                marginBottom: '2rem'
              }}
            >
              {years.map((yearItem) => (
                <Link 
                  key={yearItem}
                  to={`/winners/${yearItem}`}
                  className="px-4 py-2 sm:px-5 md:px-6 sm:py-2.5 md:py-3 rounded-full font-medium text-sm sm:text-base md:text-lg transition-all duration-300"
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '9999px',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease',
                    backgroundColor: selectedYear === yearItem ? '#ffba17' : '#F3F4F6',
                    color: selectedYear === yearItem ? 'white' : '#374151',
                    boxShadow: selectedYear === yearItem ? '0 10px 15px -3px rgba(255, 186, 23, 0.2), 0 4px 6px -2px rgba(255, 186, 23, 0.1)' : 'none',
                    textDecoration: 'none'
                  }}
                >
                  {yearItem}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter - Desktop */}
      {/* <section className="py-6 md:py-8 bg-white border-b border-gray-200 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-4">
            {memoizedWinners.map((categoryGroup, index) => (
              <button
                key={`category-${index}`}
                className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-colors ${
                  selectedCategory === index ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{
                  padding: '0.375rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  backgroundColor: selectedCategory === index ? '#ffba17' : '#F3F4F6',
                  color: selectedCategory === index ? 'white' : '#374151',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  handleCategoryChange(index);
                  const element = document.getElementById(`category-section-${index}`);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                {categoryGroup.category}
              </button>
            ))}
          </div>
          <p className="text-center text-gray-600 text-xs md:text-sm">
            <span className="font-semibold">{memoizedWinners.reduce((total, group) => total + group.subcategories.length, 0)}</span> winners across <span className="font-semibold">{memoizedWinners.length}</span> categories
          </p>
        </div>
      </section> */}
      
      {/* Category Filter - Mobile */}
      {/* <section className="py-4 sm:py-6 bg-white border-b border-gray-200 md:hidden sticky top-[60px] z-20" style={{ backgroundColor: 'white', zIndex: 20, top: '60px' }}>
        <div className="container mx-auto px-4">
          <div className="relative">
            <select 
              className="w-full py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg appearance-none text-gray-800 text-sm font-medium"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#F9FAFB',
                borderColor: '#E5E7EB',
                borderRadius: '0.5rem',
                appearance: 'none',
                color: '#1F2937',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
              value={selectedCategory}
              onChange={(e) => {
                const index = parseInt(e.target.value);
                handleCategoryChange(index);
                const element = document.getElementById(`category-section-${index}`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              {memoizedWinners.map((categoryGroup, index) => (
                <option key={`mobile-category-${index}`} value={index}>
                  {categoryGroup.category} ({categoryGroup.subcategories.length} Winners)
                </option>
              ))}
            </select>
            <div 
              className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500"
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6B7280'
              }}
            >
              <i className="fa-solid fa-chevron-down text-xs"></i>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-2">
            {selectedYear} • {winners.reduce((total, group) => total + group.subcategories.length, 0)} Winners
          </p>
        </div>
      </section> */}

      {/* Winners List */}
      <section 
        className="py-8 sm:py-12 md:py-16 bg-gray-50"
        style={{
          padding: '2rem 0',
          backgroundColor: '#F9FAFB'
        }}
      >
        <div 
          className="container mx-auto px-3 sm:px-4"
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 0.75rem'
          }}
        >
          <div 
            className="max-w-7xl mx-auto"
            style={{
              maxWidth: '80rem',
              margin: '0 auto'
            }}
          >
            {loading ? (
              <div 
                className="text-center py-8 sm:py-12"
                style={{
                  textAlign: 'center',
                  padding: '2rem 0'
                }}
              >
                <div 
                  className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 mx-auto"
                  style={{
                    animation: 'spin 1s linear infinite',
                    height: '3rem',
                    width: '3rem',
                    borderBottomWidth: '2px',
                    borderColor: '#ffba17',
                    borderRadius: '50%',
                    margin: '0 auto'
                  }}
                ></div>
                <p 
                  className="text-gray-600 mt-4 text-sm sm:text-base"
                  style={{
                    color: '#4B5563',
                    marginTop: '1rem',
                    fontSize: '0.875rem'
                  }}
                >
                  Loading winners...
                </p>
              </div>
            ) : memoizedWinners.length === 0 ? (
              <div 
                className="text-center py-8 sm:py-12 bg-white rounded-xl sm:rounded-2xl shadow-lg mx-4 sm:mx-auto"
                style={{
                  textAlign: 'center',
                  padding: '2rem 0',
                  backgroundColor: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  margin: '0 1rem'
                }}
              >
                <div 
                  className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6"
                  style={{
                    width: '4rem',
                    height: '4rem',
                    backgroundColor: '#E5E7EB',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}
                >
                  <i 
                    className="fa-solid fa-trophy text-gray-400 text-2xl sm:text-3xl"
                    style={{
                      color: '#9CA3AF',
                      fontSize: '1.5rem'
                    }}
                  ></i>
                </div>
                <h3 
                  className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4"
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '0.75rem'
                  }}
                >
                  No Winners Found
                </h3>
                <p 
                  className="text-sm sm:text-base text-gray-600 max-w-md mx-auto px-4"
                  style={{
                    fontSize: '0.875rem',
                    color: '#4B5563',
                    maxWidth: '28rem',
                    margin: '0 auto',
                    lineHeight: '1.75',
                    padding: '0 1rem'
                  }}
                >
                  There are no winners available for {selectedYear}. Please check back later or explore other years.
                </p>
              </div>
            ) : (
              <div 
                className="space-y-12 sm:space-y-16"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3rem'
                }}
              >
                {memoizedWinners.map((categoryGroup, index) => (
                  <div key={index} id={`category-section-${index}`} className="scroll-mt-24">
                    {/* Category Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 pb-3 sm:pb-4 border-b border-gray-200">
                      <h3 
                        className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-0"
                        style={{
                          fontSize: 'clamp(1.25rem, 3vw, 1.875rem)',
                          fontWeight: '700',
                          color: '#111827',
                          marginBottom: '0.5rem'
                        }}
                      >
                        {categoryGroup.category}
                      </h3>
                      <div 
                        className="text-white text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full self-start sm:self-auto"
                        style={{
                          backgroundColor: '#0f1b3c',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '9999px',
                          alignSelf: 'flex-start'
                        }}
                      >
                        {categoryGroup.subcategories.length} Winners
                      </div>
                    </div>
                    
                    <div 
                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '2rem'
                      }}
                    >
                      {categoryGroup.subcategories.map((subcategory: { name: string; winner: WinnerType }) => (
                        <div 
                          key={subcategory.winner.id}
                          className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 flex flex-col h-full transform hover:-translate-y-2"
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '1rem',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            overflow: 'hidden',
                            transition: 'all 0.5s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                          }}
                        >
                          {/* Winner Image */}
                          <div 
                            className="relative h-48 sm:h-56 overflow-hidden"
                            style={{
                              position: 'relative',
                              height: '14rem',
                              overflow: 'hidden'
                            }}
                          >
                            <img 
                              src={subcategory.winner.image} 
                              alt={subcategory.winner.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.7s ease'
                              }}
                              loading="lazy"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                // Prevent infinite error loops
                                if (target.src !== PLACEHOLDER_IMAGE) {
                                  target.src = PLACEHOLDER_IMAGE;
                                }
                              }}
                            />
                            {/* Gradient Overlay */}
                            <div 
                              className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                              style={{
                                position: 'absolute',
                                inset: '0',
                                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent, transparent)',
                                opacity: '0',
                                transition: 'opacity 0.3s ease'
                              }}
                            ></div>
                            {/* Winner Badge */}
                            {/* <div 
                              className="absolute top-4 right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1.5 text-xs font-bold rounded-full shadow-lg"
                              style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: 'linear-gradient(90deg, #ffba17 0%, #c2410c 100%)',
                                color: 'white',
                                padding: '0.375rem 0.75rem',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                borderRadius: '9999px',
                                boxShadow: '0 10px 15px -3px rgba(249, 115, 22, 0.3)'
                              }}
                            >
                              <i className="fa-solid fa-trophy mr-1"></i>
                              Winner
                            </div> */}
                            {/* Year Badge */}
                            <div 
                              className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800 px-2.5 py-1 text-xs font-semibold rounded-full"
                              style={{
                                position: 'absolute',
                                top: '1rem',
                                left: '1rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(4px)',
                                color: '#1F2937',
                                padding: '0.25rem 0.625rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                borderRadius: '9999px'
                              }}
                            >
                              {selectedYear}
                            </div>
                          </div>
                          
                          {/* Content */}
                          <div 
                            className="p-6 flex-grow flex flex-col"
                            style={{
                              padding: '1.5rem',
                              flexGrow: 1,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            {/* Category Badge */}
                            <div 
                              className="inline-flex items-center bg-orange-50 text-orange-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-4 w-fit"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: '#FFF7ED',
                                color: '#C2410C',
                                padding: '0.375rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                marginBottom: '1rem',
                                width: 'fit-content'
                              }}
                            >
                              <i className="fa-solid fa-award mr-2"></i>
                              {subcategory.name}
                            </div>
                            
                            {/* Winner Name */}
                            <h4 
                              className="text-xl font-bold text-gray-900 mb-3 transition-colors duration-300"
                              style={{
                                fontSize: '1.25rem',
                                fontWeight: '700',
                                color: '#111827',
                                marginBottom: '0.75rem',
                                transition: 'color 0.3s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#ffba17'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#111827'}
                            >
                              {subcategory.winner.name}
                            </h4>
                            
                            {/* Organization */}
                            <div 
                              className="text-sm text-blue-600 font-medium mb-4"
                              style={{
                                fontSize: '0.875rem',
                                color: '#2563EB',
                                fontWeight: '500',
                                marginBottom: '1rem'
                              }}
                            >
                              {/* <i className="fa-solid fa-building mr-2"></i> */}
                              {categoryGroup.category}
                            </div>
                            
                            {/* Description */}
                            <p 
                              className="text-sm text-gray-600 leading-relaxed flex-grow mb-6"
                              style={{
                                fontSize: '0.875rem',
                                color: '#4B5563',
                                lineHeight: '1.75',
                                flexGrow: 1,
                                marginBottom: '1.5rem'
                              }}
                            >
                              {subcategory.winner.description.length > 120 
                                ? `${subcategory.winner.description.substring(0, 120)}...` 
                                : subcategory.winner.description}
                            </p>
                            
                            {/* Footer */}
                            <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                              <div className="flex items-center text-xs text-gray-500">
                                <i className="fa-solid fa-calendar mr-1"></i>
                                {selectedYear} Winner
                              </div>
                              {/* <button className="group/btn bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-full text-xs font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg">
                                <span className="flex items-center">
                                  View Details 
                                  <i className="fa-solid fa-arrow-right ml-2 group-hover/btn:translate-x-1 transition-transform duration-300"></i>
                                </span>
                              </button> */}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20"
        style={{
          padding: '5rem 0',
          background: 'linear-gradient(90deg, #0f1b3c 0%, #1a2d5c 100%)'
        }}
      >
        <div 
          className="container mx-auto px-4 text-center"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem',
            textAlign: 'center'
          }}
        >
          <div 
            className="max-w-4xl mx-auto"
            style={{
              maxWidth: '56rem',
              margin: '0 auto'
            }}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              style={{
                fontSize: 'clamp(2.25rem, 5vw, 3rem)',
                fontWeight: '700',
                color: 'white',
                marginBottom: '1.5rem',
                lineHeight: '1.2'
              }}
            >
              Join the Digital Excellence Journey
            </h2>
            <p 
              className="text-xl text-gray-300 mb-8 leading-relaxed"
              style={{
                fontSize: '1.25rem',
                color: '#D1D5DB',
                marginBottom: '2rem',
                lineHeight: '1.75'
              }}
            >
              Be part of Tanzania's premier platform celebrating innovation and digital transformation.
              Submit your nomination for the next Tanzania Digital Awards.
            </p>
            <div 
              className="flex flex-col sm:flex-row justify-center gap-4"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <a 
                href="/vote/"
                className="inline-flex items-center px-8 py-4 text-white rounded-full font-semibold text-lg transition-all duration-300 shadow-lg"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: '#ffba17',
                  color: 'white',
                  borderRadius: '9999px',
                  fontWeight: '600',
                  fontSize: '1.125rem',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 10px 15px -3px rgba(255, 186, 23, 0.3), 0 4px 6px -2px rgba(255, 186, 23, 0.2)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5a815'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffba17'}
              >
                <i 
                  className="fa-solid fa-trophy mr-3"
                  style={{
                    marginRight: '0.75rem'
                  }}
                ></i>
                Nominate Now
              </a>
              <Link 
                to="/categories"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: 'white',
                  color: '#111827',
                  borderRadius: '9999px',
                  fontWeight: '600',
                  fontSize: '1.125rem',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
              >
                <i 
                  className="fa-solid fa-list-check mr-3"
                  style={{
                    marginRight: '0.75rem'
                  }}
                ></i>
                View Categories
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Winners;